import React, { useState, useEffect, useCallback } from "react";
import {
  getContractTypes,
  getPlayerPositions,
  createDocumentTemplate,
  searchService,
} from "../../services/api"; // Adjust path as necessary
import CustomDropdown from "./CustomDropdown";

const UploadTemplateModal = ({ isOpen, onClose, onSuccess }) => {
  const [documentTitle, setDocumentTitle] = useState("");
  const [contractType, setContractType] = useState("");
  const [playerPosition, setPlayerPosition] = useState("");
  const [documentFile, setDocumentFile] = useState(null);
  const [contractTypes, setContractTypes] = useState([]);
  const [playerPositions, setPlayerPositions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFetchingLookups, setIsFetchingLookups] = useState(false);

  const fetchLookups = useCallback(async () => {
    setIsFetchingLookups(true);
    setError(null);
    try {
      const [typesResponse, positionsResponse] = await Promise.all([
        getContractTypes(),
        searchService.getPositions(),
      ]);
      setContractTypes(typesResponse.data || []);
      setPlayerPositions(positionsResponse.data || []);
    } catch (err) {
      console.error("Error fetching lookups:", err);
      setError("Failed to load contract types or player positions.");
    } finally {
      setIsFetchingLookups(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchLookups();
      // Reset form on open
      setDocumentTitle("");
      setContractType("");
      setPlayerPosition("");
      setDocumentFile(null);
      setError(null);
    }
  }, [isOpen, fetchLookups]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (
      file &&
      file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      setDocumentFile(file);
      setError(null); // Clear previous file errors
    } else {
      setDocumentFile(null);
      setError("Please upload a valid .docx file.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!documentFile) {
      setError("Please select a .docx file to upload.");
      return;
    }
    if (!documentTitle.trim()) {
      setError("Document title is required.");
      return;
    }
    if (!contractType) {
      setError("Contract type is required.");
      return;
    }

    setError(null);
    setIsLoading(true);

    const formData = new FormData();
    formData.append("name", documentTitle);
    formData.append("template_type", contractType);
    if (playerPosition) {
      formData.append("player_position", playerPosition);
    }
    formData.append("document_file", documentFile);

    try {
      await createDocumentTemplate(formData);
      onSuccess(); // Callback to refresh parent component or show success message
      onClose(); // Close modal on success
    } catch (err) {
      console.error("Error uploading template:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.errors?.join(", ") ||
        "Failed to upload template. Please check the details and try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
      <div className="relative mx-auto p-8 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-semibold text-gray-800">
            Upload New Template
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="documentTitle"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Document Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="documentTitle"
              value={documentTitle}
              onChange={(e) => setDocumentTitle(e.target.value)}
              className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="e.g., Standard Player Contract"
              required
            />
          </div>

          <div>
            <CustomDropdown
              label="Contract Type *"
              labelClassName="block text-sm font-medium text-gray-700 mb-1"
              options={contractTypes}
              value={contractType}
              onChange={(value) => setContractType(value)}
              placeholder="Select contract type..."
              displayProperty="label"
              valueProperty="value"
              disabled={isFetchingLookups}
              className="mt-1 block w-full px-4 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <CustomDropdown
              label="Player Position (Optional)"
              labelClassName="block text-sm font-medium text-gray-700 mb-1"
              options={playerPositions}
              value={playerPosition}
              onChange={(value) => setPlayerPosition(value)}
              placeholder="Select player position (if applicable)..."
              displayProperty="name"
              valueProperty="id"
              disabled={isFetchingLookups}
              className="mt-1 block w-full px-4 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label
              htmlFor="documentFile"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Upload .docx File <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              id="documentFile"
              onChange={handleFileChange}
              className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            />
            <p className="mt-1 text-xs text-gray-500">
              Max file size: 50MB. File type: .docx
            </p>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
              {error}
            </p>
          )}
          {isFetchingLookups && (
            <p className="text-sm text-blue-600">
              Loading selection options...
            </p>
          )}

          <div className="flex items-center justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-6 py-2.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || isFetchingLookups}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
            >
              {isLoading && (
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
              {isLoading ? "Saving..." : "Save template"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadTemplateModal;
